import React from 'react';
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {vscDarkPlus} from 'react-syntax-highlighter/dist/esm/styles/prism';
import { MOB_EVENT_TYPE_EVENT } from '../../config/const';
import { mob } from '../../utils/logger';

const MemoryApiGuide = ({ onSectionChange }) => {
  return (
    <div className="doc-content">
      <div className="quickstart-header">
        <p className="quickstart-category">Getting Started</p>
        <h1>Memory API Guide - Python</h1>
      </div>

      <p>Learn how to use MemU's Memory API for manual memory management and advanced use cases with Python.</p>

      <h2 id="overview">Overview</h2>
      <p>
        The Memory API provides granular control over memory operations, allowing you to manually manage
        conversation storage and retrieval. This is ideal for advanced use cases where you need custom
        memory handling logic or want to integrate with existing Python applications.
      </p>

      <div className="feature-highlight-box">
        <h4>🎯 What you'll learn</h4>
        <ul>
          <li>How to set up MemU Memory API in Python</li>
          <li>Step-by-step memory operations workflow</li>
          <li>Best practices for memory management</li>
          <li>Integration with popular AI frameworks</li>
        </ul>
      </div>

      <h2 id="step-1-installation">Step 1: Installation and Setup</h2>

      <h3>Install the MemU Python SDK</h3>
      <div className="code-block">
        <SyntaxHighlighter
          language="bash"
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: 0,
            background: 'transparent',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          pip install memu-py
        </SyntaxHighlighter>
      </div>

      <h3>Get your API key</h3>
      <div className="setup-steps">
        <div className="step-item">
          <div className="step-number">1</div>
          <div className="step-content">
            <p>Visit <a href="https://app.memu.so/" target="_blank" rel="noopener noreferrer"
              onClick={() => mob(
                'memu_pro_ev_quick_start_to_console_click',
                MOB_EVENT_TYPE_EVENT,
                {
                  source: 'memory',
                  source_lang: 'py'
                },
                true)
              }>https://app.memu.so/</a> and sign in</p>
          </div>
        </div>
        <div className="step-item">
          <div className="step-number">2</div>
          <div className="step-content">
            <p>Copy your API key from the dashboard</p>
          </div>
        </div>
        <div className="step-item">
          <div className="step-number">3</div>
          <div className="step-content">
            <p>Set it as an environment variable:</p>
            <div className="code-block">
              <SyntaxHighlighter
                language="bash"
                style={vscDarkPlus}
                customStyle={{
                  margin: 0,
                  padding: 0,
                  background: 'transparent',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}
              >
                export MEMU_API_KEY="your_api_key_here"
              </SyntaxHighlighter>
            </div>
          </div>
        </div>
      </div>

      <div className="code-block">
        <SyntaxHighlighter
          language="python"
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: 0,
            background: 'transparent',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          {`import os
import time
from memu import MemuClient

# Initialize MemU client
memu_client = MemuClient(
    base_url="https://api.memu.so",
    api_key=os.getenv("MEMU_API_KEY")
)

print("✅ MemU client initialized successfully!")`}
        </SyntaxHighlighter>
      </div>

      <h2 id="step-3-workflow">Step 3: Understanding the Memory Workflow</h2>

      <div className="key-points-box">
        <h4>🔄 Memory API Workflow</h4>
        <p>The Memory API follows this pattern for optimal performance:</p>
        <ol>
          <li><strong>Retrieve</strong> existing memories before starting a conversation</li>
          <li><strong>Build context</strong> using retrieved memories</li>
          <li><strong>Process</strong> user input with your AI model</li>
          <li><strong>Memorize</strong> the conversation for future use</li>
        </ol>
      </div>

      <h2 id="step-4-retrieve-memories">Step 4: Retrieve Existing Memories</h2>
      <p>Before starting a conversation, retrieve existing user memories to build context:</p>

      <div className="code-block">
        <SyntaxHighlighter
          language="python"
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: 0,
            background: 'transparent',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          {`# Step 4: Retrieve user memories
def retrieve_user_memories(user_id, agent_id):
    try:
        memories = memu_client.retrieve_default_categories(
            user_id=user_id,
            agent_id=agent_id
        )
        
        print('📚 Retrieved memories:', memories)
        return memories
    except Exception as error:
        print('❌ Error retrieving memories:', error)
        return {'categories': []}

# Example usage
user_id = "user_123"
agent_id = "assistant_001"
user_memories = retrieve_user_memories(user_id, agent_id)`}
        </SyntaxHighlighter>
      </div>

      <h2 id="step-5-build-context">Step 5: Build AI Context with Memories</h2>

      <p>Use retrieved memories to create a personalized system prompt:</p>

      <div className="code-block">
        <SyntaxHighlighter
          language="python"
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: 0,
            background: 'transparent',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          {`# Step 5: Build context from memories
def build_system_prompt(memories):
    system_prompt = "You are a helpful AI assistant. Here's what you know about the user:\\n\\n"
    
    if memories and 'categories' in memories:
        for category in memories['categories']:
            if category.get('summary'):
                system_prompt += f"**{category['name']}:** {category['summary']}\\n\\n"
    
    return system_prompt

# Example usage
system_prompt = build_system_prompt(user_memories)
print('🧠 System prompt built:', system_prompt)`}
        </SyntaxHighlighter>
      </div>

      <h2 id="step-6-ai-interaction">Step 6: Process User Input with AI</h2>

      <p>Now use the context to have a conversation. Here's an example with OpenAI:</p>

      <div className="code-block">
        <SyntaxHighlighter
          language="python"
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: 0,
            background: 'transparent',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          {`# Step 6: AI interaction with context
import openai

def chat_with_ai(system_prompt, user_message, conversation_history=None):
    if conversation_history is None:
        conversation_history = []
        
    try:
        messages = [{"role": "system", "content": system_prompt}]
        messages.extend(conversation_history)
        messages.append({"role": "user", "content": user_message})

        response = openai.ChatCompletion.create(
            model="gpt-4o",
            messages=messages
        )

        ai_response = response.choices[0].message.content
        print('🤖 AI Response:', ai_response)
        
        return ai_response
    except Exception as error:
        print('❌ Error with AI:', error)
        return "Sorry, I encountered an error."

# Example usage
user_message = "Hi, how are you today?"
ai_response = chat_with_ai(system_prompt, user_message)`}
        </SyntaxHighlighter>
      </div>

      <h2 id="step-7-memorize">Step 7: Save Conversation to Memory</h2>

      <p>After the conversation, save it to MemU for future use:</p>

      <div className="code-block">
        <SyntaxHighlighter
          language="python"
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: 0,
            background: 'transparent',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          {`# Step 7: Save conversation to memory
import time

def save_conversation(conversation, user_id, agent_id):
    try:
        response = memu_client.memorize_conversation(
            conversation=conversation,
            user_id=user_id,
            user_name="Demo User",
            agent_id=agent_id,
            agent_name="AI Assistant"
        )

        print('💾 Conversation saved! Task ID:', response.task_id)
        
        # Wait for completion
        wait_for_completion(response.task_id)
        
    except Exception as error:
        print('❌ Error saving conversation:', error)

def wait_for_completion(task_id):
    while True:
        try:
            status = memu_client.get_task_status(task_id)
            print('📊 Task status:', status.status)
            
            if status.status in ['SUCCESS', 'FAILURE', 'REVOKED']:
                break
            
            # Wait 2 seconds before checking again
            time.sleep(2)
        except Exception as error:
            print('❌ Error checking task status:', error)
            break

# Example: Build conversation context for saving
conversation_context = [
    {"role": "user", "content": user_message},
    {"role": "assistant", "content": ai_response}
]

save_conversation(conversation_context, user_id, agent_id)`}
        </SyntaxHighlighter>
      </div>

      <h3>Retrieve Memories</h3>
      <p>Retrieve stored memories and build personalized AI interactions using:</p>
      <ul>
        <li><code>retrieve_default_categories()</code> - Gets all user memories organized by categories</li>
      </ul>

      <p><strong>Complete Example:</strong> This is a MemU-powered interactive AI assistant that uses user memories to provide personalized responses.</p>

      <div className="key-points-box">
        <h4>🔑 Key Points</h4>
        <ul>
          <li><strong>Pre-conversation Retrieval:</strong> Retrieve memories before interacting with the language model to build context</li>
          <li><strong>No Mid-conversation Calls:</strong> No additional memory retrieval calls are needed during the conversation flow</li>
          <li><strong>Post-conversation Storage:</strong> Memory saving should also be performed outside the conversation loop for optimal performance</li>
        </ul>
      </div>

      <div className="code-block">
        <SyntaxHighlighter
          language="python"
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: 0,
            background: 'transparent',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          {`# Complete Memory API Example
import os
import time
import openai
from memu import MemuClient

# Initialize clients
memu_client = MemuClient(
    base_url="https://api.memu.so",
    api_key=os.getenv("MEMU_API_KEY")
)

def run_memory_chat():
    user_id = "user_123"
    agent_id = "assistant_001"
    
    # Step 1: Retrieve existing memories
    print('🔍 Retrieving user memories...')
    user_memories = memu_client.retrieve_default_categories(
        user_id=user_id,
        agent_id=agent_id
    )
    
    # Step 2: Build system prompt with memories
    system_prompt = "You are a helpful AI assistant. Here's what you know about the user:\\n\\n"
    
    if user_memories and hasattr(user_memories, 'categories'):
        for category in user_memories.categories:
            if category.summary:
                system_prompt += f"**{category.name}:** {category.summary}\\n\\n"
    
    print('🧠 Context built with user memories')
    
    # Step 3: Start interactive conversation
    conversation_history = []
    
    while True:
        user_input = input('👤 You: ')
        if user_input.lower() == 'quit':
            print('👋 Goodbye!')
            break
        
        try:
            # Step 4: Get AI response with context
            messages = [{"role": "system", "content": system_prompt}]
            messages.extend(conversation_history)
            messages.append({"role": "user", "content": user_input})
            
            response = openai.ChatCompletion.create(
                model="gpt-4o",
                messages=messages
            )
            
            ai_response = response.choices[0].message.content
            print(f'🤖 Assistant: {ai_response}')
            
            # Step 5: Update conversation history
            conversation_history.append({"role": "user", "content": user_input})
            conversation_history.append({"role": "assistant", "content": ai_response})
            
            # Step 6: Save to memory (every few exchanges to optimize)
            if len(conversation_history) >= 4:
                print('💾 Saving conversation to memory...')
                memu_client.memorize_conversation(
                    conversation=conversation_history,
                    user_id=user_id,
                    user_name="Demo User",
                    agent_id=agent_id,
                    agent_name="AI Assistant"
                )
                conversation_history = []  # Clear after saving
                
        except Exception as error:
            print('❌ Error:', error)

# Run the example
if __name__ == "__main__":
    run_memory_chat()`}
        </SyntaxHighlighter>
      </div>

      <h2 id="best-practices">Best Practices</h2>

      <div className="key-points-box">
        <h4>💡 Memory API Best Practices</h4>
        <ul>
          <li><strong>Pre-conversation Retrieval:</strong> Always retrieve memories before starting a conversation</li>
          <li><strong>Batch Memorization:</strong> Save conversations in batches (every 2-4 exchanges) for better performance</li>
          <li><strong>Error Handling:</strong> Implement proper error handling for API calls</li>
          <li><strong>Context Management:</strong> Keep conversation history manageable to avoid token limits</li>
        </ul>
      </div>

      <h2 id="integration-examples">Framework Integration Examples</h2>

      <h3>FastAPI Integration</h3>
      <div className="code-block">
        <SyntaxHighlighter
          language="python"
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: 0,
            background: 'transparent',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          {`# FastAPI endpoint with Memory API
from fastapi import FastAPI, HTTPException
from pydantic import BaseModel
from memu import MemuClient
import os

app = FastAPI()
memu_client = MemuClient(
    base_url="https://api.memu.so",
    api_key=os.getenv("MEMU_API_KEY")
)

class ChatRequest(BaseModel):
    user_id: str
    message: str
    agent_id: str = "default_agent"

@app.post("/chat")
async def chat_endpoint(request: ChatRequest):
    try:
        # Retrieve memories
        memories = memu_client.retrieve_default_categories(
            user_id=request.user_id,
            agent_id=request.agent_id
        )
        
        # Build context and process with your AI model
        # ... (your AI processing logic here)
        
        # Save conversation
        memu_client.memorize_conversation(
            conversation=[
                {"role": "user", "content": request.message},
                {"role": "assistant", "content": ai_response}
            ],
            user_id=request.user_id,
            agent_id=request.agent_id,
            user_name="Web User",
            agent_name="Web Assistant"
        )
        
        return {"response": ai_response}
    except Exception as error:
        raise HTTPException(status_code=500, detail=str(error))`}
        </SyntaxHighlighter>
      </div>

      <h2 id="comparison">Memory API vs Response API</h2>
      <div className="comparison-box">
        <h4>📋 When to Use Memory API</h4>
        <div className="comparison-grid">
          <div className="comparison-column">
            <h5>Memory API (Manual Control)</h5>
            <ul>
              <li>✅ Full control over memory operations</li>
              <li>✅ Custom memory processing logic</li>
              <li>✅ Integration with existing AI models</li>
              <li>✅ Framework flexibility (OpenAI, Anthropic, etc.)</li>
              <li>✅ Advanced memory workflows</li>
            </ul>
          </div>
          <div className="comparison-column">
            <h5>Response API (Automatic)</h5>
            <ul>
              <li>✅ Automatic memory management</li>
              <li>✅ Simplified integration</li>
              <li>✅ Built-in AI model</li>
              <li>✅ Quick prototyping</li>
              <li>✅ No external AI API needed</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Need Help?</h2>
      <p>If you have any questions about the Memory API Python integration, feel free to reach out:</p>

      <div className="help-links">
        <div className="help-item">
          <h3>Discord</h3>
          <p>Join our developer community</p>
          <a href="https://discord.gg/hQZntfGsbJ" className="help-link">Join Discord →</a>
        </div>

        <div className="help-item">
          <h3>GitHub</h3>
          <p>Browse examples and ask questions</p>
          <a href="#github" className="help-link">Visit GitHub →</a>
        </div>

        <div className="help-item">
          <h3>Support</h3>
          <p>Get direct technical support</p>
          <a href="mailto:info@nevamind.ai" className="help-link">Contact Support →</a>
        </div>
      </div>
    </div>
  );
};

export default MemoryApiGuide;

